import type { TFunction } from 'i18next';

interface OtpErrorInput {
  code?: string;
  retryAfterSeconds?: number;
}

/** Localized, pluralized "wait N" string (e.g. "2 minutes", "45 seconds"). */
export function formatRetryAfter(t: TFunction, seconds: number): string {
  const safeSeconds = Math.max(0, Math.ceil(seconds));
  if (safeSeconds < 60) {
    return t('time.seconds', { count: safeSeconds });
  }
  return t('time.minutes', { count: Math.ceil(safeSeconds / 60) });
}

/**
 * Maps a backend auth error (code + optional retry window) to a friendly,
 * localized message. Never surfaces raw backend strings — unknown codes fall
 * back to a generic, reassuring message.
 */
export function getOtpErrorMessage(t: TFunction, { code, retryAfterSeconds }: OtpErrorInput): string {
  switch (code) {
    case 'RATE_LIMITED':
      return retryAfterSeconds && retryAfterSeconds > 0
        ? t('auth.errors.tooManyOtpRequests', { wait: formatRetryAfter(t, retryAfterSeconds) })
        : t('auth.errors.tooManyOtpRequestsGeneric');
    case 'OTP_DAILY_CAP_EXCEEDED':
      return t('auth.errors.dailyCapExceeded');
    case 'OTP_MAX_ATTEMPTS_EXCEEDED':
      return t('auth.errors.maxAttempts');
    case 'SMS_DELIVERY_FAILED':
      return t('auth.errors.smsDeliveryFailed');
    case 'INVALID_OTP':
      return t('auth.errors.invalidOtp');
    default:
      return t('auth.errors.generic');
  }
}
